import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { money as fmtMoney, initials, avatarColor, titleCase } from "./format.js";
import { api } from "./api.js";

/* ---------------------------------------------------------------- toasts */

const ToastCtx = createContext({ push: () => {} });
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((message, tone = "ok") => {
    const id = Math.random().toString(36).slice(2);
    setItems((xs) => [...xs, { id, message, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === "err" ? 7000 : 4000);
  }, []);
  const dismiss = (id) => setItems((xs) => xs.filter((x) => x.id !== id));

  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="toasts">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <span>{t.message}</span>
            <span className="tx" onClick={() => dismiss(t.id)}>
              ×
            </span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---------------------------------------------------------------- data loading */

export function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const alive = useRef(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    alive.current = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(loader)
      .then((data) => alive.current && setState({ data, loading: false, error: null }))
      .catch((error) => alive.current && setState({ data: null, loading: false, error }));
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return {
    ...state,
    reload: () => setNonce((n) => n + 1),
    setData: (data) => setState((s) => ({ ...s, data })),
  };
}

export const Loading = ({ label = "Loading…" }) => (
  <div className="loading">
    <span className="spinner" />
    <span className="sm">{label}</span>
  </div>
);

export const ErrorPanel = ({ error, onRetry }) => (
  <div className="err-panel">
    {error?.message || String(error)}
    {onRetry && (
      <>
        {" "}
        <span className="linky" onClick={onRetry}>
          Try again
        </span>
      </>
    )}
  </div>
);

/** Renders loading / error / empty states so pages don't each reinvent them. */
export function Async({ state, children, empty }) {
  if (state.loading) return <Loading />;
  if (state.error) return <ErrorPanel error={state.error} onRetry={state.reload} />;
  if (empty && (!state.data || (Array.isArray(state.data) && !state.data.length))) return empty;
  return children(state.data);
}

/* ---------------------------------------------------------------- layout */

export const TopBar = ({ crumb, title, children }) => (
  <div className="top">
    <div>
      {crumb && <div className="crumb">{crumb}</div>}
      <h1>{title}</h1>
    </div>
    <div className="grow" />
    {children}
  </div>
);

export const Wrap = ({ children, style }) => (
  <div className="wrap" style={style}>
    {children}
  </div>
);

/**
 * `cols` lands as an inline grid-template-columns, which no media query can
 * override — so the mobile stylesheet collapses `.grid` to one column with
 * !important instead. Pass `keepCols` for the rare grid that must stay
 * multi-column on a phone; it opts out via `.grid-keep`.
 */
export const Grid = ({ cols, children, style, className = "", keepCols }) => (
  <div
    className={`grid ${keepCols ? "grid-keep" : ""} ${className}`}
    style={{ gridTemplateColumns: typeof cols === "number" ? `repeat(${cols},1fr)` : cols, ...style }}
  >
    {children}
  </div>
);

export function Card({ title, right, children, bare, style, className = "" }) {
  return (
    <div className={`card ${className}`} style={style}>
      {(title || right) && (
        <div className="hd">
          {typeof title === "string" ? <h3>{title}</h3> : title}
          <div className="grow" />
          {right}
        </div>
      )}
      {bare ? children : <div className="bd">{children}</div>}
    </div>
  );
}

export const KPI = ({ label, value, sub, tone }) => (
  <div className="kpi">
    <div className="k">{label}</div>
    <div className="v" style={tone ? { color: `var(--${tone})` } : undefined}>
      {value}
    </div>
    {sub && <div className={`d ${tone === "danger" ? "down" : "muted"}`}>{sub}</div>}
  </div>
);

export const Sep = () => <div className="sep" />;

export const Row = ({ children, style, gap = 10, className = "" }) => (
  <div className={`row ${className}`} style={{ gap, ...style }}>
    {children}
  </div>
);

export const Ribbon = ({ tone, children }) => (
  <div className={`ribbon ${tone === "warn" ? "w" : ""}`}>{children}</div>
);

export const Empty = ({ icon = "▤", children }) => (
  <div className="empty">
    <div className="big">{icon}</div>
    {children}
  </div>
);

/* ---------------------------------------------------------------- atoms */

export const Money = ({ v, sym = "$", bold }) => {
  const el = <span className="num">{fmtMoney(v, sym)}</span>;
  return bold ? <b className="num">{fmtMoney(v, sym)}</b> : el;
};

const BADGE_CLASS = {
  draft: "b-draft",
  sent: "b-sent",
  viewed: "b-viewed",
  paid: "b-paid",
  partial: "b-partial",
  overdue: "b-overdue",
  void: "b-arch",
  active: "b-active",
  complete: "b-paid",
  archived: "b-arch",
  paused: "b-arch",
  accepted: "b-paid",
  declined: "b-overdue",
  invited: "b-partial",
  disabled: "b-arch",
  unbilled: "b-sent",
  billed: "b-paid",
  internal: "b-draft",
  received: "b-sent",
};

export const Badge = ({ status, label }) => (
  <span className={`badge ${BADGE_CLASS[status] || "b-draft"}`}>{label || titleCase(String(status || ""))}</span>
);

/**
 * An <img> for an endpoint behind the auth header. A plain `src` would 401,
 * because the browser's own image request carries no Authorization header.
 */
export function AuthImage({ path, alt = "", style, fallback = null, onResolved }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  // Kept in a ref so a fresh callback identity can't retrigger the fetch. The
  // effect must depend on `path` alone.
  const report = useRef(onResolved);
  report.current = onResolved;

  useEffect(() => {
    if (!path) return;
    let alive = true;
    let created = null;
    setFailed(false);
    setUrl(null);
    api
      .blobUrl(path)
      .then((u) => {
        created = u;
        if (alive) {
          setUrl(u);
          // Lets a caller say which image the user is actually looking at. Without
          // this the fallback is indistinguishable from the real thing whenever the
          // two happen to look alike — which is exactly how a missing logo went
          // unnoticed.
          report.current?.(true);
        } else URL.revokeObjectURL(u);
      })
      .catch(() => {
        if (!alive) return;
        setFailed(true);
        report.current?.(false);
      });
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);

  if (!path || failed) return fallback;
  if (!url) return <span className="spinner" style={{ width: 14, height: 14 }} />;
  return <img src={url} alt={alt} style={style} />;
}

export const Avatar = ({ name = "", size = 30 }) => (
  <div
    className="av"
    style={{ width: size, height: size, background: avatarColor(name), fontSize: Math.round(size / 2.6) }}
  >
    {initials(name)}
  </div>
);

export const Bar = ({ pct = 0, warn = false }) => (
  <div className="bar">
    <i style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: warn ? "var(--warn)" : undefined }} />
  </div>
);

export const Stack = ({ children, gap = 10 }) => (
  <div className="stack" style={{ gap }}>
    {children}
  </div>
);

/* ---------------------------------------------------------------- table */

/**
 * `stacked` (default on) emits data-label on every cell from its column label.
 * Under 640px the stylesheet uses those labels to redraw each row as a small
 * card, so a 9-column invoice table stays readable on a phone without any page
 * needing to know about it. Pass stacked={false} for a table that genuinely
 * needs to stay a grid — the document line items, say — and it keeps the
 * existing horizontal scroll instead.
 */
export function Table({ columns, rows, onRowClick, empty, keyFn, footer, stacked = true }) {
  const cols = columns.filter(Boolean);
  return (
    <div className="tbl-wrap">
      <table className={stacked ? "stacked" : ""}>
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th key={c.key || i} className={c.align === "right" ? "right" : ""} style={c.width ? { width: c.width } : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, ri) => (
              <tr
                key={keyFn ? keyFn(row) : row.id || ri}
                className={onRowClick ? "clickable" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {cols.map((c, ci) => (
                  <td
                    key={c.key || ci}
                    className={c.align === "right" ? "right" : ""}
                    data-label={typeof c.label === "string" ? c.label : undefined}
                  >
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={cols.length}>{empty || <Empty>Nothing here yet</Empty>}</td>
            </tr>
          )}
          {footer}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- controls */

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="tabs">
    {tabs.map((t) => (
      <div key={t.key} className={`tab ${value === t.key ? "on" : ""}`} onClick={() => onChange(t.key)}>
        {t.label}
      </div>
    ))}
  </div>
);

export const Chips = ({ options, value, onChange }) => (
  <div className="chips">
    {options.map((o) => (
      <span key={o.key} className={`chip ${value === o.key ? "on" : ""}`} onClick={() => onChange(o.key)}>
        {o.label}
        {o.count !== undefined && o.count !== null ? ` · ${o.count}` : ""}
      </span>
    ))}
  </div>
);

export const Field = ({ label, children, hint }) => (
  <div>
    {label && <label className="f">{label}</label>}
    {children}
    {hint && <div className="tiny muted" style={{ marginTop: 4 }}>{hint}</div>}
  </div>
);

export const Fields = ({ cols, children, className = "", style }) => (
  <div className={`fields ${cols ? `f${cols}` : ""} ${className}`} style={style}>
    {children}
  </div>
);

export const Input = ({ ...props }) => <input className="i" {...props} />;
export const Select = ({ children, ...props }) => (
  <select className="i" {...props}>
    {children}
  </select>
);
export const Textarea = ({ ...props }) => <textarea className="i" {...props} />;

export const Check = ({ label, checked, onChange, disabled }) => (
  <label className="row sm" style={{ gap: 8 }}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    <span>{label}</span>
  </label>
);

export function Button({ kind = "", sm, children, busy, ...props }) {
  return (
    <button className={`btn ${kind} ${sm ? "sm" : ""}`} disabled={busy || props.disabled} {...props}>
      {busy && <span className="spinner" style={{ width: 12, height: 12 }} />}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------- modal */

export function Modal({ title, onClose, children, footer, width = 560 }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="mask" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: width }}>
        <div className="mh">
          <h2>{title}</h2>
          <span className="x" onClick={onClose}>
            ×
          </span>
        </div>
        <div className="mb">{children}</div>
        {footer !== null && (
          <div className="mf">
            {footer || (
              <Button kind="" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Small helper for controlled forms: const [f, set] = useForm({...}) */
export function useForm(initial) {
  const [form, setForm] = useState(initial);
  const set = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);
  return [form, set, setForm];
}

/* ---------------------------------------------------------------- charts */

export function SparkBars({ data, format = (v) => v }) {
  const max = Math.max(1, ...data.map((d) => Number(d.amount) || 0));
  return (
    <>
      <div className="sparkbar">
        {data.map((d, i) => (
          <div key={i} title={`${d.label}: ${format(d.amount)}`}>
            <i style={{ height: `${Math.max(2, ((Number(d.amount) || 0) / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="axis">
        {data.map((d, i) => (
          <div key={i}>{d.label}</div>
        ))}
      </div>
    </>
  );
}

export function BarList({ rows, format = (v) => v }) {
  const max = Math.max(1, ...rows.map((r) => Number(r.value) || 0));
  return (
    <div style={{ display: "grid", gap: 13 }}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="row sm">
            <span>{r.label}</span>
            <div className="grow" />
            <b className="num">{format(r.value)}</b>
          </div>
          <Bar pct={((Number(r.value) || 0) / max) * 100} />
        </div>
      ))}
    </div>
  );
}
